// =============================================
// src/utils/vehicleBusinessRules.js
// LEAN BUSINESS RULES & STATUS MANAGEMENT
// Enhanced with TrafficInsurance Logic
// =============================================

import { Badge } from 'react-bootstrap';

// ============================
// CORE BUSINESS RULES
// ============================

/**
 * Business Rule: SOLD status için gerekli belgeler
 * @param {Array} vehicleDocuments - Araç belgeleri array'i
 * @returns {boolean} - Noter onaylı satış sözleşmesi var mı?
 */
export const canVehicleBeSold = (vehicleDocuments) => {
    if (!vehicleDocuments || vehicleDocuments.length === 0) return false;

    return vehicleDocuments.some(doc => doc.category === 'satis_sozlesmesi');
};

/**
 * Business Rule: ARCHIVED status için gerekli belgeler ve bilgiler
 * @param {Object} vehicle - Vehicle object
 * @param {Array} vehicleDocuments - Araç belgeleri array'i
 * @returns {boolean} - Sigorta iptal poliçesi + iade miktarı var mı?
 */
export const canVehicleBeArchived = (vehicle, vehicleDocuments) => {
    if (!vehicleDocuments || vehicleDocuments.length === 0) return false;

    const hasInsuranceCancellationDoc = vehicleDocuments.some(doc =>
        doc.category === 'sigorta_iptal_policesi' ||
        (doc.category === 'trafik_sigortasi' && doc.notes?.toLowerCase().includes('iptal'))
    );

    const hasRefundAmount = vehicle?.trafficInsurance?.refundAmount > 0;

    return hasInsuranceCancellationDoc && hasRefundAmount;
};

// ============================
// TRAFFIC INSURANCE BUSINESS RULES
// ============================

/**
 * Business Rule: Trafik sigortası gerekli mi?
 * @param {Object} vehicle - Vehicle object
 * @returns {boolean} - Trafik sigortası zorunlu mu?
 */
export const isTrafficInsuranceRequired = (vehicle) => {
    // Tüm araçlar için trafik sigortası yasal zorunluluk
    return vehicle && vehicle.status !== 'archived';
};

/**
 * Business Rule: Sigorta iptal edilebilir mi?
 * @param {Object} vehicle - Vehicle object
 * @returns {boolean} - Sigorta iptal edilebilir mi?
 */
export const canCancelTrafficInsurance = (vehicle) => {
    if (!vehicle?.trafficInsurance?.hasInsurance) return false;

    // Sadece satılan veya arşivlenen araçlar için iptal edilebilir
    return ['sold', 'archived'].includes(vehicle.status);
};

/**
 * Business Rule: Sigorta iptal bilgileri tamamlanmış mı?
 * @param {Object} trafficInsurance - TrafficInsurance object
 * @returns {boolean} - İptal bilgileri complete mi?
 */
export const isInsuranceCancellationComplete = (trafficInsurance) => {
    if (!trafficInsurance?.isCancelled) return false;

    return trafficInsurance.cancellationDate &&
        trafficInsurance.cancellationReason &&
        trafficInsurance.refundAmount >= 0; // 0 olabilir
};

/**
 * Business Rule: Sigorta maliyeti hesaplama
 * @param {Object} trafficInsurance - TrafficInsurance object
 * @returns {Object} - Cost breakdown
 */
export const calculateInsuranceCost = (trafficInsurance) => {
    if (!trafficInsurance) {
        return { totalPaid: 0, refunded: 0, netCost: 0, refundPercentage: 0 };
    }

    const totalPaid = trafficInsurance.insuranceAmount || 0;
    const refunded = trafficInsurance.refundAmount || 0;
    const netCost = totalPaid - refunded;

    return {
        totalPaid,
        refunded,
        netCost,
        refundPercentage: totalPaid > 0 ? ((refunded / totalPaid) * 100).toFixed(1) : 0
    };
};

/**
 * Business Rule: Sigorta durumu analizi
 * @param {Object} vehicle - Vehicle object
 * @returns {Object} - Insurance status analysis
 */
export const analyzeInsuranceStatus = (vehicle) => {
    if (!vehicle?.trafficInsurance) {
        return {
            status: 'missing',
            message: 'Trafik sigortası bilgisi yok',
            severity: 'error',
            actionRequired: 'Sigorta bilgilerini ekleyin'
        };
    }

    const insurance = vehicle.trafficInsurance;

    // Sigorta yok
    if (!insurance.hasInsurance) {
        return {
            status: 'no_insurance',
            message: 'Trafik sigortası yok',
            severity: 'error',
            actionRequired: 'Yasal zorunluluk - Sigorta yaptırın'
        };
    }

    // Sigorta var ama iptal gerekiyor (SOLD/ARCHIVED)
    if (['sold', 'archived'].includes(vehicle.status) && !insurance.isCancelled) {
        return {
            status: 'cancellation_required',
            message: 'Sigorta iptal edilmeli',
            severity: 'warning',
            actionRequired: 'Araç satıldı/arşivlendi - Sigortayı iptal edin'
        };
    }

    // İptal edilmiş ama bilgiler eksik
    if (insurance.isCancelled && !isInsuranceCancellationComplete(insurance)) {
        return {
            status: 'cancellation_incomplete',
            message: 'İptal bilgileri eksik',
            severity: 'warning',
            actionRequired: 'İptal belgesi ve iade miktarını tamamlayın'
        };
    }

    // Sigorta süresi dolmuş (varsa endDate)
    if (insurance.endDate && new Date(insurance.endDate) < new Date()) {
        return {
            status: 'expired',
            message: 'Sigorta süresi dolmuş',
            severity: 'error',
            actionRequired: 'Sigortayı yenileyin'
        };
    }

    // Her şey tamam
    return {
        status: 'complete',
        message: 'Sigorta durumu uygun',
        severity: 'success',
        actionRequired: null
    };
};

/**
 * Validate TrafficInsurance form data
 * @param {Object} trafficInsurance - Traffic insurance data
 * @param {string} vehicleStatus - Vehicle status
 * @returns {Object} - Validation result
 */
export const validateTrafficInsuranceForm = (trafficInsurance, vehicleStatus = 'active') => {
    const errors = [];
    const warnings = [];

    if (!trafficInsurance) {
        errors.push('Trafik sigortası bilgisi gereklidir');
        return { isValid: false, errors, warnings };
    }

    // Basic validation
    if (trafficInsurance.hasInsurance) {
        if (!trafficInsurance.startDate) {
            errors.push('Sigorta başlangıç tarihi gereklidir');
        }

        if (!trafficInsurance.insuranceCompany) {
            errors.push('Sigorta şirketi seçilmelidir');
        }

        if (!trafficInsurance.insuranceAmount || trafficInsurance.insuranceAmount <= 0) {
            warnings.push('Sigorta bedeli girilmemiş');
        }
    }

    // Status-based validation
    if (['sold', 'archived'].includes(vehicleStatus)) {
        if (trafficInsurance.hasInsurance && !trafficInsurance.isCancelled) {
            warnings.push('Araç satıldı/arşivlendi - Sigorta iptal edilmelidir');
        }

        if (trafficInsurance.isCancelled) {
            if (!trafficInsurance.cancellationDate) {
                errors.push('İptal tarihi gereklidir');
            }

            if (!trafficInsurance.cancellationReason) {
                errors.push('İptal nedeni seçilmelidir');
            }

            if (trafficInsurance.refundAmount < 0) {
                errors.push('İade miktarı negatif olamaz');
            }
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
};

/**
 * Get form field requirements based on vehicle status
 * @param {string} vehicleStatus - Vehicle status
 * @returns {Object} - Required and optional fields
 */
export const getFormFieldRequirements = (vehicleStatus = 'active') => {
    const base = {
        required: ['trafficInsurance.hasInsurance'],
        optional: ['trafficInsurance.notes'],
        hidden: []
    };

    if (vehicleStatus === 'active' || vehicleStatus === 'reserved') {
        base.required.push(
            'trafficInsurance.startDate',
            'trafficInsurance.insuranceCompany'
        );
        base.optional.push(
            'trafficInsurance.endDate',
            'trafficInsurance.insuranceAmount',
            'trafficInsurance.policyNumber'
        );
        base.hidden.push(
            'trafficInsurance.cancellationDate',
            'trafficInsurance.cancellationReason',
            'trafficInsurance.refundAmount'
        );
    }

    if (vehicleStatus === 'sold' || vehicleStatus === 'archived') {
        base.required.push(
            'trafficInsurance.startDate',
            'trafficInsurance.insuranceCompany',
            'trafficInsurance.cancellationDate',
            'trafficInsurance.cancellationReason'
        );
        base.optional.push(
            'trafficInsurance.refundAmount',
            'trafficInsurance.cancellationNotes'
        );
    }

    return base;
};

/**
 * Get field visibility rules for TrafficInsuranceForm
 * @param {string} vehicleStatus - Vehicle status
 * @param {Object} trafficInsurance - Traffic insurance data
 * @returns {Object} - Field visibility rules
 */
export const getInsuranceFieldVisibility = (vehicleStatus, trafficInsurance = {}) => {
    const hasInsurance = trafficInsurance.hasInsurance;
    const shouldShowCancellation = hasInsurance && ['sold', 'archived'].includes(vehicleStatus);

    return {
        showBasicFields: hasInsurance,
        showCancellationFields: shouldShowCancellation,
        showCancellationDetails: shouldShowCancellation && trafficInsurance.isCancelled,
        requireCancellation: ['sold', 'archived'].includes(vehicleStatus) && hasInsurance
    };
};

/**
 * Get insurance status badge
 * @param {Object} vehicle - Vehicle object
 * @returns {JSX.Element} - Insurance status badge
 */
export const getInsuranceStatusBadge = (vehicle) => {
    const analysis = analyzeInsuranceStatus(vehicle);

    const variants = {
        'complete': 'success',
        'missing': 'danger',
        'no_insurance': 'danger',
        'cancellation_required': 'warning',
        'cancellation_incomplete': 'warning',
        'expired': 'danger'
    };

    const icons = {
        'complete': '✅',
        'missing': '❌',
        'no_insurance': '🚫',
        'cancellation_required': '⚠️',
        'cancellation_incomplete': '⚠️',
        'expired': '⏰'
    };

    return (
        <Badge bg={variants[analysis.status]} title={analysis.message}>
            {icons[analysis.status]} Sigorta
        </Badge>
    );
};

// ============================
// STATUS MANAGEMENT
// ============================

/**
 * Status label mapping
 * @param {string} status - Vehicle status
 * @returns {string} - Türkçe label
 */
export const getStatusLabel = (status) => {
    const labels = {
        'active': 'Stokta',
        'reserved': 'Rezerve',
        'sold': 'Satıldı',
        'archived': 'Arşiv'
    };
    return labels[status] || status;
};

/**
 * Status variant mapping (Bootstrap colors)
 * @param {string} status - Vehicle status
 * @returns {string} - Bootstrap variant
 */
export const getStatusVariant = (status) => {
    const variants = {
        'active': 'success',
        'reserved': 'warning',
        'sold': 'danger',
        'archived': 'secondary'
    };
    return variants[status] || 'secondary';
};

/**
 * Enhanced Status Badge with Business Rules Validation
 * @param {Object} vehicle - Vehicle object
 * @param {Array} vehicleDocuments - Vehicle documents array
 * @returns {JSX.Element} - Enhanced status badge with warning icons
 */
export const getEnhancedStatusBadge = (vehicle, vehicleDocuments) => {
    const status = vehicle?.status;
    if (!status) return null;

    // Business rule validations
    const warnings = [];

    // Document warnings
    if (status === 'sold' && !canVehicleBeSold(vehicleDocuments)) {
        warnings.push('Noter onaylı satış sözleşmesi eksik!');
    }

    if (status === 'archived' && !canVehicleBeArchived(vehicle, vehicleDocuments)) {
        warnings.push('Sigorta iptal poliçesi veya iade miktarı eksik!');
    }

    // Insurance warnings
    const insuranceAnalysis = analyzeInsuranceStatus(vehicle);
    if (insuranceAnalysis.severity === 'error' || insuranceAnalysis.severity === 'warning') {
        warnings.push(insuranceAnalysis.message);
    }

    return (
        <div className="d-flex align-items-center gap-2">
            <Badge bg={getStatusVariant(status)}>
                {getStatusLabel(status)}
            </Badge>
            {warnings.length > 0 && (
                <Badge bg="danger" title={warnings.join(' | ')}>
                    ⚠️ {warnings.length}
                </Badge>
            )}
        </div>
    );
};

/**
 * Vehicle status validation with warnings
 * @param {string} newStatus - New status to validate
 * @param {Object} vehicle - Vehicle object
 * @param {Array} vehicleDocuments - Vehicle documents array
 * @returns {Object} - { isValid: boolean, warningMessage: string }
 */
export const validateStatusChange = (newStatus, vehicle, vehicleDocuments) => {
    const warnings = [];

    // Document warnings
    if (newStatus === 'sold' && !canVehicleBeSold(vehicleDocuments)) {
        warnings.push('Noter onaylı satış sözleşmesi eksik!');
    }

    if (newStatus === 'archived' && !canVehicleBeArchived(vehicle, vehicleDocuments)) {
        warnings.push('Sigorta iptal poliçesi veya iade miktarı eksik!');
    }

    // Insurance warnings for status changes
    if (['sold', 'archived'].includes(newStatus)) {
        const insuranceAnalysis = analyzeInsuranceStatus(vehicle);
        if (insuranceAnalysis.status === 'missing' || insuranceAnalysis.status === 'no_insurance') {
            warnings.push('Trafik sigortası bilgisi eksik!');
        }

        if (newStatus === 'sold' && !canCancelTrafficInsurance(vehicle)) {
            warnings.push('Sigorta iptal bilgileri tamamlanmalı!');
        }
    }

    const warningMessage = warnings.length > 0 ?
        `UYARI: ${warnings.join(' ')} Yine de devam etmek istiyor musunuz?` : '';

    return {
        isValid: true, // Her zaman true - sadece uyarı, engelleme yok
        warningMessage,
        warnings
    };
};

// ============================
// DOCUMENT MANAGEMENT
// ============================

/**
 * Get critical documents for current vehicle status
 * @param {string} status - Vehicle status
 * @returns {Array} - Array of critical document categories
 */
export const getCriticalDocumentsForStatus = (status) => {
    switch (status) {
        case 'sold':
            return ['satis_sozlesmesi', 'sigorta_iptal_policesi'];
        case 'archived':
            return ['sigorta_iptal_policesi', 'trafik_sigortasi'];
        default:
            return ['ruhsat', 'trafik_sigortasi'];
    }
};

/**
 * Check if document is critical for vehicle status
 * @param {Object} document - Document object
 * @param {string} vehicleStatus - Vehicle status
 * @returns {boolean} - Is this document critical for the status?
 */
export const isDocumentCriticalForStatus = (document, vehicleStatus) => {
    const criticalDocs = getCriticalDocumentsForStatus(vehicleStatus);

    if (vehicleStatus === 'sold' && document.category === 'satis_sozlesmesi') {
        return document.title?.toLowerCase().includes('noter') ||
            document.notes?.toLowerCase().includes('noter');
    }

    if (vehicleStatus === 'archived' && document.category === 'trafik_sigortasi') {
        return document.notes?.toLowerCase().includes('iptal');
    }

    return criticalDocs.includes(document.category);
};